import React from 'react';
import {Link} from 'react-router-dom';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const {FiUser,FiCalendar,FiGlobe,FiMessageCircle,FiTrendingUp,FiDollarSign,FiCheckCircle,FiStar}=FiIcons;

const TutoringServices=()=> {
  const features=[ 
    {icon: FiUser,title: 'Qualified & Experienced Tutors',description: 'Expert tutors with advanced qualifications'},
    {icon: FiCalendar,title: 'Flexible Scheduling',description: 'Learn Anytime,Anywhere'},
    {icon: FiGlobe,title: 'Curriculum-Aligned Tutoring',description: 'Cambridge,IB,IEB,and more'},
    {icon: FiMessageCircle,title: 'Interactive Learning',description: 'Real-Time Feedback'},
    {icon: FiTrendingUp,title: 'Exam Preparation',description: 'Focus on Skill Development'},
    {icon: FiDollarSign,title: 'Affordable Pricing',description: 'Student-Friendly Rates'} 
  ];

  const curriculums=[ 
    {title: 'Cambridge Curriculum',levels: 'Primary,Lower Secondary,IGCSE,AS/A Levels'},
    {title: 'IB Curriculum',levels: 'PYP,MYP,DP (International Baccalaureate)'},
    {title: 'IEB Curriculum',levels: 'Independent Examinations Board programs'},
    {title: 'National & International Boards',levels: 'Subject support for school and college students'},
    {title: 'University-Level Tutoring',levels: 'Advanced subject guidance and research support'},
    {title: 'Professional Certification Support',levels: 'Support for professional exams and certification programs'} 
  ];

  const subjects=[ 
    'Mathematics & Statistics – Algebra,Calculus,Probability,Data Analysis',
    'Science Subjects – Physics,Chemistry,Biology',
    'Business & Economics – Accounting,Finance,Marketing,Management',
    'Computer Science & Engineering – Programming,Data Science,IT',
    'Humanities & Social Sciences – History,Sociology,Psychology,Political Science',
    'Languages – English,Literature,and Academic Writing Skills' 
  ];

  const benefits=[ 
    'Improved Understanding: Get clear explanations of concepts and detailed solutions to deepen your knowledge.',
    'Workload Management: Receive support to meet deadlines and manage heavy academic workloads.',
    'Skill Development: Learn how to structure assignments,write effectively,and cite sources properly.',
    'Better Grades: Achieve higher marks through superior quality work and improved understanding.' 
  ];

  const steps=[ 
    {number: '1',title: 'Choose Your Subject & Curriculum',description: 'Tell us what area you need help with.'},
    {number: '2',title: 'Get Matched with a Tutor',description: 'We connect you with the best-fit tutor for your needs.'},
    {number: '3',title: 'Learn in Interactive Online Sessions',description: 'Personalized teaching,real-time guidance,and improved results.'} 
  ];

  const testimonials=[ 
    {text: "The tutoring sessions were excellent. My tutor explained every concept with patience,and I finally understood math!",author: "Sarah",rating: 5},
    {text: "Thanks to Tutor Hub Online,I performed much better in my IB exams. The personalized sessions made learning so much easier.",author: "Ahmed",rating: 5} 
  ];

  return ( 
    <div> 
      {/* Hero Section */} 
      <section className="bg-gradient-to-br from-primary to-blue-700 text-white py-20"> 
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8"> 
          <div className="text-center"> 
            <h1 className="text-4xl lg:text-5xl font-bold mb-6"> 
              Personalized Tutoring Services for Every Student 
            </h1> 
            <h2 className="text-xl lg:text-2xl text-blue-100 mb-8 max-w-4xl mx-auto"> 
              One-to-one and group tutoring sessions designed to strengthen your knowledge,boost confidence,and improve academic performance. 
            </h2> 
            <div className="flex flex-col sm:flex-row gap-4 justify-center"> 
              <Link 
                to="/book-tutor" 
                className="bg-accent text-black px-8 py-3 rounded-lg font-semibold hover:bg-yellow-400 transition-colors" 
              > 
                👉 Book a Tutor 
              </Link> 
              <Link 
                to="/contact" 
                className="border-2 border-white text-white px-8 py-3 rounded-lg font-semibold hover:bg-white hover:text-primary transition-colors" 
              > 
                👉 Free Consultation 
              </Link> 
            </div> 
          </div> 
        </div> 
      </section> 

      {/* What is Tutoring */} 
      <section className="py-16"> 
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8"> 
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center"> 
            <div className="space-y-6"> 
              <h2 className="text-3xl font-bold text-gray-900">What is Tutoring?</h2> 
              <p className="text-gray-600 text-lg"> 
                Tutoring at Tutor Hub Online means more than just homework help. It's about learning in a way that works for you. 
              </p> 
              <ul className="space-y-3 text-gray-600"> 
                <li>• Our tutors explain complex topics step by step.</li> 
                <li>• They guide you through assignments and coursework.</li> 
                <li>• They help you develop study skills,writing skills,and critical thinking so you can complete future assignments independently.</li> 
              </ul> 
              <p className="text-gray-700 font-medium"> 
                Tutoring is not just about grades — it's about building lifelong learning confidence. 
              </p> 
            </div> 
            <div className="relative"> 
              <img 
                src="https://i.ibb.co/W4cg0pRY/Tutoring-Services.jpg" 
                alt="Tutoring Services" 
                className="w-full h-96 object-cover rounded-2xl shadow-lg" 
              /> 
            </div> 
          </div> 
        </div> 
      </section> 

      {/* Why Choose Us */} 
      <section className="py-16 bg-muted"> 
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8"> 
          <div className="text-center mb-12"> 
            <h2 className="text-3xl font-bold text-gray-900 mb-4">Why Choose Tutor Hub Online for Tutoring?</h2> 
          </div> 
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8"> 
            {features.map((feature,index)=> ( 
              <div 
                key={index} 
                className="bg-white p-6 rounded-xl shadow-sm text-center" 
              > 
                <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center mx-auto mb-4"> 
                  <SafeIcon icon={feature.icon} className="w-6 h-6 text-primary" /> 
                </div> 
                <h3 className="text-lg font-semibold text-gray-900 mb-2">{feature.title}</h3> 
                <p className="text-gray-600 text-sm">{feature.description}</p> 
              </div> 
            ))} 
          </div> 
        </div> 
      </section> 

      {/* Curriculum Levels */} 
      <section className="py-16"> 
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8"> 
          <div className="text-center mb-12"> 
            <h2 className="text-3xl font-bold text-gray-900 mb-4">Tutoring by Curriculum Levels</h2> 
          </div> 
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6"> 
            {curriculums.map((curriculum,index)=> ( 
              <div 
                key={index} 
                className="bg-white p-6 rounded-xl shadow-sm border" 
              > 
                <h3 className="text-lg font-semibold text-gray-900 mb-2">{curriculum.title}</h3> 
                <p className="text-gray-600">{curriculum.levels}</p> 
              </div> 
            ))} 
          </div> 
        </div> 
      </section> 

      {/* Subjects */} 
      <section className="py-16 bg-muted"> 
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8"> 
          <div className="text-center mb-12"> 
            <h2 className="text-3xl font-bold text-gray-900 mb-4">Subjects We Cover</h2> 
          </div> 
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6"> 
            {subjects.map((subject,index)=> ( 
              <div 
                key={index} 
                className="bg-white p-6 rounded-xl shadow-sm" 
              > 
                <p className="text-gray-700">{subject}</p> 
              </div> 
            ))} 
          </div> 
        </div> 
      </section> 

      {/* Benefits */} 
      <section className="py-16"> 
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8"> 
          <div className="text-center mb-12"> 
            <h2 className="text-3xl font-bold text-gray-900 mb-4">Benefits of Tutoring (and Assignment Support Combined)</h2> 
          </div> 
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6"> 
            {benefits.map((benefit,index)=> ( 
              <div key={index} className="flex items-start space-x-3"> 
                <SafeIcon icon={FiCheckCircle} className="w-6 h-6 text-green-500 mt-1 flex-shrink-0" /> 
                <span className="text-gray-700">{benefit}</span> 
              </div> 
            ))} 
          </div> 
        </div> 
      </section> 

      {/* How It Works */} 
      <section className="py-16 bg-muted"> 
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8"> 
          <div className="text-center mb-12"> 
            <h2 className="text-3xl font-bold text-gray-900 mb-4">How It Works (3 Easy Steps)</h2> 
          </div> 
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8"> 
            {steps.map((step,index)=> ( 
              <div key={index} className="text-center"> 
                <div className="w-16 h-16 bg-primary text-white rounded-full flex items-center justify-center text-2xl font-bold mx-auto mb-4"> 
                  {step.number} 
                </div> 
                <h3 className="text-xl font-semibold text-gray-900 mb-3">{step.title}</h3> 
                <p className="text-gray-600">{step.description}</p> 
              </div> 
            ))} 
          </div> 
          <div className="text-center mt-12"> 
            <div className="flex flex-col sm:flex-row gap-4 justify-center"> 
              <Link 
                to="/book-tutor" 
                className="bg-primary text-white px-8 py-3 rounded-lg font-semibold hover:bg-blue-700 transition-colors" 
              > 
                👉 Book a Session 
              </Link> 
              <Link 
                to="/contact" 
                className="border-2 border-primary text-primary px-8 py-3 rounded-lg font-semibold hover:bg-primary hover:text-white transition-colors" 
              > 
                👉 Free Consultation 
              </Link> 
            </div> 
          </div> 
        </div> 
      </section> 

      {/* Testimonials */} 
      <section className="py-16"> 
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8"> 
          <div className="text-center mb-12"> 
            <h2 className="text-3xl font-bold text-gray-900 mb-4">Testimonials</h2> 
          </div> 
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8"> 
            {testimonials.map((testimonial,index)=> ( 
              <div key={index} className="bg-white p-8 rounded-xl shadow-sm"> 
                <div className="flex items-center space-x-1 mb-4"> 
                  {[...Array(testimonial.rating)].map((_,i)=> ( 
                    <SafeIcon key={i} icon={FiStar} className="w-5 h-5 text-yellow-400 fill-current" /> 
                  ))} 
                </div> 
                <p className="text-gray-600 mb-4 italic">"{testimonial.text}"</p> 
                <p className="text-gray-900 font-semibold">– {testimonial.author}</p> 
              </div> 
            ))} 
          </div> 
        </div> 
      </section> 

      {/* CTA Section */} 
      <section className="py-16 bg-primary text-white"> 
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center"> 
          <h2 className="text-3xl font-bold mb-4"> 
            🎯 Don't let difficult subjects hold you back! 
          </h2> 
          <p className="text-xl text-blue-100 mb-8 max-w-4xl mx-auto"> 
            With Tutor Hub Online's Tutoring Services,you'll receive personalized guidance,flexible learning,and expert support to achieve your academic goals. 
          </p> 
          <Link 
            to="/book-tutor" 
            className="bg-accent text-black px-8 py-4 rounded-lg font-semibold hover:bg-yellow-400 transition-colors text-lg" 
          > 
            👉 Book Your Tutoring Session Today! 
          </Link> 
        </div> 
      </section> 
    </div> 
  ); 
};

export default TutoringServices;